import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Button,
  Chip,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Fab
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Search as SearchIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchProductsStart,
  setProductSearchTerm,
  setProductActiveFilter,
  setProductPagination
} from '../../store/reducers/productSlice';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(2),
    },
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
}));

const ProductList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const {
    products,
    productLoading,
    productError,
    productPagination,
    productSearchTerm,
    productActiveFilter
  } = useSelector((state: RootState) => state.product);

  const [showFilters, setShowFilters] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(productSearchTerm);

  useEffect(() => {
    dispatch(fetchProductsStart());
  }, [dispatch, productPagination.currentPage, productPagination.pageSize, productSearchTerm, productActiveFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    dispatch(setProductSearchTerm(localSearchTerm));
  };

  const handleActiveFilterChange = (event: any) => {
    const value = event.target.value;
    dispatch(setProductActiveFilter(value === '' ? null : value === 'true'));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setProductPagination({
      ...productPagination,
      currentPage: newPage + 1
    }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    dispatch(setProductPagination({
      ...productPagination,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleEdit = (id: string) => {
    navigate(`/products/edit/${id}`);
  };

  const handleAdd = () => {
    navigate('/products/add');
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  if (productError) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading products: {productError}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.headerContainer}>
        <Typography variant="h4" component="h1">
          Products
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add New Product
        </Button>
      </Box>

      {/* Filters */}
      <Paper className={classes.paper}>
        <Box className={classes.searchContainer}>
          <Box className={classes.searchBox}>
            <TextField
              size="small"
              placeholder="Search products..."
              value={localSearchTerm}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearchSubmit} size="small">
                    <SearchIcon />
                  </IconButton>
                )
              }}
            />
          </Box>
          <IconButton onClick={toggleFilters}>
            <FilterIcon />
          </IconButton>
        </Box>

        {showFilters && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={productActiveFilter === null ? '' : productActiveFilter.toString()}
                  onChange={handleActiveFilterChange}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Products Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>UofM</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading products...
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>{product.Name}</TableCell>
                    <TableCell>{product.Description}</TableCell>
                    <TableCell>{product.UoM?.Name || '-'}</TableCell>
                    <TableCell>{product.Value}</TableCell>
                    <TableCell>
                      <Chip
                        label={product.Active ? 'Active' : 'Inactive'}
                        color={product.Active ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(product.id!)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={productPagination.totalCount}
          rowsPerPage={productPagination.pageSize}
          page={productPagination.currentPage - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      <Fab
        color="primary"
        aria-label="add"
        onClick={handleAdd}
        className={classes.fab}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default ProductList; 